import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Search,
  Clear,
  Person,
  Description,
  FilterList,
  Check,
} from '@mui/icons-material';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetUserTemplatesQuery, useSearchProvidersQuery } from '../store/hooks';
import type { UserTemplateInfo } from '@store/generated/admin';

interface TemplateDiscoveryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string, displayName?: string) => void;
  organizationId: string;
  existingTemplateIds?: string[];
}

export function TemplateDiscoveryDialog({
  open,
  onClose,
  onSelect,
  organizationId,
  existingTemplateIds = [],
}: TemplateDiscoveryDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<UserTemplateInfo | null>(null);
  const [customDisplayName, setCustomDisplayName] = useState('');

  // Fetch templates with filters
  const {
    data: templatesData,
    isLoading: templatesLoading,
  } = useGetUserTemplatesQuery(
    {
      userId: userFilter,
      searchQuery: searchQuery.trim() || undefined,
    },
    { skip: !open }
  );

  // Search for users to filter by
  const {
    data: userSearchResults,
    isLoading: userSearchLoading,
  } = useSearchProvidersQuery(userSearchQuery, {
    skip: !showUserSearch || userSearchQuery.trim().length === 0,
  });

  const templates = useMemo(() => {
    const allTemplates = templatesData?.templates || [];
    // Filter out templates that are already added to the organization
    return allTemplates.filter(t => !existingTemplateIds.includes(t.template_id));
  }, [templatesData, existingTemplateIds]);

  const handleSelectUser = useCallback((userId: string, userName: string) => {
    setUserFilter(userId);
    setShowUserSearch(false);
    setUserSearchQuery(userName); // Show the user's name in the search field
    // RTK Query will automatically refetch when userId parameter changes
  }, []);

  const handleClearUserFilter = useCallback(() => {
    setUserFilter(null);
    setUserSearchQuery('');
    setShowUserSearch(false);
    // RTK Query will automatically refetch when userId parameter changes
  }, []);

  const handleSelectTemplate = useCallback((template: UserTemplateInfo) => {
    setSelectedTemplate(template);
    setCustomDisplayName(template.display_name || template.template_name);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedTemplate) {
      onSelect(selectedTemplate.template_id, customDisplayName || undefined);
      handleClose();
    }
  }, [selectedTemplate, customDisplayName, onSelect]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setUserFilter(null);
    setUserSearchQuery('');
    setShowUserSearch(false);
    setSelectedTemplate(null);
    setCustomDisplayName('');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle>
        Discover Templates
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Browse and search user-created templates to add to your organization
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
        {/* Search and Filter Controls */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search templates by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ position: 'relative', minWidth: 250 }}>
            <TextField
              fullWidth
              placeholder="Filter by user..."
              value={userSearchQuery}
              onChange={(e) => {
                setUserSearchQuery(e.target.value);
                setShowUserSearch(true);
              }}
              onFocus={() => userSearchQuery && setShowUserSearch(true)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                ),
                endAdornment: userFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearUserFilter}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* User Search Dropdown */}
            {showUserSearch && userSearchQuery && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  zIndex: 10,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
                elevation={3}
              >
                {userSearchLoading ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : userSearchResults && userSearchResults.length > 0 ? (
                  <List dense>
                    {userSearchResults.map((user: any) => (
                      <ListItemButton
                        key={user.provider_id}
                        onClick={() => handleSelectUser(user.provider_id, user.name || user.email)}
                      >
                        <ListItemText
                          primary={user.name || 'Unknown'}
                          secondary={user.email}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    No users found
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Selected Template Info */}
        {selectedTemplate && (
          <Alert
            severity="info"
            icon={<Check />}
            action={
              <Button size="small" onClick={() => setSelectedTemplate(null)}>
                Change
              </Button>
            }
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Selected: {selectedTemplate.template_name}
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Display Name (Optional)"
              value={customDisplayName}
              onChange={(e) => setCustomDisplayName(e.target.value)}
              helperText="Custom name to display for this template in your organization"
              sx={{ mt: 1 }}
            />
          </Alert>
        )}

        {/* Template List */}
        <Box sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {templatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {existingTemplateIds.length > 0 && templatesData?.templates?.length
                  ? 'All available templates have already been added to this organization'
                  : searchQuery || userFilter
                  ? 'No templates found matching your filters'
                  : 'No templates available'}
              </Typography>
            </Box>
          ) : (
            <List>
              {templates.map((template, index) => (
                <Box key={template.template_id}>
                  {index > 0 && <Divider />}
                  <ListItemButton
                    selected={selectedTemplate?.template_id === template.template_id}
                    onClick={() => handleSelectTemplate(template)}
                    sx={{ py: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {template.template_name}
                          </Typography>
                          {template.display_name && template.display_name !== template.template_name && (
                            <Chip
                              label={template.display_name}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person sx={{ fontSize: 14 }} />
                              <Typography variant="caption">
                                {template.creator_name || 'Unknown creator'}
                              </Typography>
                            </Box>
                            {template.sections_count !== undefined && (
                              <Typography variant="caption">
                                {template.sections_count} section{template.sections_count !== 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" color="text.disabled">
                              ID: {template.template_id}
                            </Typography>
                            {template.modified_on && (
                              <Typography variant="caption" color="text.disabled">
                                Modified {formatRelativeTime(new Date(template.modified_on * 1000))}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
          )}
        </Box>

        {/* Result Count */}
        {!templatesLoading && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {templates.length} template{templates.length !== 1 ? 's' : ''} available
            {templatesData?.total_count && templatesData.total_count > templates.length && (
              <> ({templatesData.total_count - templates.length} already added)</>
            )}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedTemplate}
        >
          Add Template
        </Button>
      </DialogActions>
    </Dialog>
  );
}